import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FilePlus,
  FolderKanban,
  MessageSquare,
  Plus,
  MoreHorizontal,
  Trash2,
  Loader2,
  Calendar,
  ToggleLeft,
  LogOut,
  UsersRound,
  GitCompare,
  Wallet,
  UserCog,
  ChevronsUpDown,
  HelpCircle,
  BookOpen,
  ClipboardList,
  BarChart3,
  Mail,
  Settings,
  Building2,
  ChevronDown,
  ChevronRight,
  MessageSquarePlus,
  Pencil,
} from 'lucide-react';
import { WordIcon } from '../icons/WordIcon';
import { WORD_ADDIN_MARKETPLACE_URL } from '../../config/external-links';
import logo from '../../assets/logo.png';
import { useWorkspace } from '../../workspace/WorkspaceProvider';
import { PoweredByQueri } from '../../workspace/PoweredByQueri';
import { useBoundStore } from '../../store';
import { MeetingsDrawer } from '../meetings-drawer';
import { useFeatureToggle } from '../../hooks/useFeatureToggle';
import { logout } from '../../services/Authentication';
import { renameQAgentChat } from '../../services/QAgent';
import ShareProjectDialog from './ShareProjectDialog';
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useI18n } from '../../i18n';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { useOnboarding } from '../../onboarding/useOnboarding';

export const QAgentSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { launchTour } = useOnboarding();
  const [meetingsDrawerOpen, setMeetingsDrawerOpen] = useState(false);

  // Extract project/chat IDs from URL path
  // /qagent/project/:projectId or /qagent/:chatId
  const { routeProjectId, routeChatId } = useMemo(() => {
    const path = location.pathname;
    const projectMatch = path.match(/^\/qagent\/project\/([^/]+)/);
    const chatMatch = path.match(/^\/qagent\/([^/]+)$/);

    return {
      routeProjectId: projectMatch?.[1] || null,
      routeChatId: chatMatch?.[1] || null,
    };
  }, [location.pathname]);

  // Also check location.state for project context (set when navigating from project route)
  const stateProjectId = (location.state as { fromProjectId?: string })?.fromProjectId || null;

  // Store
  const projects = useBoundStore((state) => state.projects);
  const trial = useBoundStore((state) => state.user?.trial);
  const fetchProjects = useBoundStore((state) => state.fetchProjects);
  const isProjectsLoading = useBoundStore((state) => state.isProjectsLoading);
  const createProject = useBoundStore((state) => state.createProject);
  const allChats = useBoundStore((state) => state.qagentChats);
  const fetchQAgentChats = useBoundStore((state) => state.fetchQAgentChats);
  const deleteQAgentChatStore = useBoundStore((state) => state.deleteQAgentChat);
  const createQAgentChatStore = useBoundStore((state) => state.createQAgentChat);
  const updateQAgentChatTitle = useBoundStore((state) => state.updateQAgentChatTitle);
  const isLoadingChats = useBoundStore((state) => state.isQAgentLoading);
  const getUser = useBoundStore((state) => state.getUser);
  const removeAccessToken = useBoundStore((state) => state.removeAccessToken);
  const removeRefreshToken = useBoundStore((state) => state.removeRefreshToken);
  const user = getUser();

  const deleteProjectStore = useBoundStore((state) => state.deleteProject);

  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  // Delete project dialog state
  const [projectToDelete, setProjectToDelete] = useState<typeof projects[0] | null>(null);
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  // Feature toggles
  const { isEnabled: isMeetingsEnabled } = useFeatureToggle('ai-meeting-assistant');
  const { isEnabled: canManageFeatures } = useFeatureToggle('feature-toggles-admin');
  const { isEnabled: isQueriAdmin } = useFeatureToggle('queri-admin');
  const { isEnabled: canAccessModelSettings } = useFeatureToggle('model-settings-admin');
  const { displayName: workspaceName, logoUrl: workspaceLogoUrl } = useWorkspace();


  // Derived state: quick chats (no project) and chat-to-project mapping
  // Chats without a user project, including email conversations whose
  // documents live in an internal (hidden) container
  const quickChats = useMemo(
    () => allChats.filter((chat) => !chat.projectId || chat.projectHidden),
    [allChats],
  );
  // A project can hold several chats: newest first under each project
  const chatsByProject = useMemo(() => {
    const map = new Map<string, typeof allChats>();
    for (const chat of allChats) {
      if (!chat.projectId || chat.projectHidden) continue;
      map.set(chat.projectId, [...(map.get(chat.projectId) ?? []), chat]);
    }
    for (const list of map.values()) {
      list.sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
    }
    return map;
  }, [allChats]);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);
  const [sharingProject, setSharingProject] = useState<{ id: string; name: string } | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  // Determine which project is active based on current chat's projectId
  const activeProjectId = useMemo(() => {
    // Priority: route projectId > state projectId > current chat's projectId
    if (routeProjectId) return routeProjectId;
    if (stateProjectId) return stateProjectId;

    // Look up current chat's projectId
    if (routeChatId) {
      const currentChat = allChats.find((c) => c.id === routeChatId);
      return currentChat?.projectId || null;
    }

    return null;
  }, [routeProjectId, stateProjectId, routeChatId, allChats]);

  // Fetch projects and chats on mount
  useEffect(() => {
    fetchProjects();
    fetchQAgentChats();
  }, [fetchProjects, fetchQAgentChats]);

  const handleProjectClick = (projectId: string) => {
    navigate(`/qagent/project/${projectId}`);
  };

  const handleQuickChatClick = (chatId: string) => {
    navigate(`/qagent/${chatId}`);
  };

  const handleNewChat = () => {
    navigate('/');
  };

  const handleNewContract = () => {
    navigate('/', { state: { openGenerateDialog: true } });
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;

    setIsCreatingProject(true);
    try {
      const project = await createProject({ name: newProjectName.trim() });
      if (project) {
        setShowNewProjectDialog(false);
        setNewProjectName('');
        // Navigate to the new project's chat
        navigate(`/qagent/project/${project.id}`);
      } else {
        // Pull the latest error the store wrote (e.g. trial cap hit) so the
        // user sees the specific reason instead of a silent no-op.
        const error = useBoundStore.getState().projectsError;
        toast.error(error || t.nav.newProjectDialog.createFailed);
      }
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleDeleteChat = async (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const success = await deleteQAgentChatStore(chatId);
    if (success && routeChatId === chatId) {
      navigate('/');
    }
  };

  const toggleProject = (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const handleNewChatInProject = async (projectId: string) => {
    const chat = await createQAgentChatStore({ projectId });
    if (chat) {
      setExpandedProjects((prev) => new Set(prev).add(projectId));
      navigate(`/qagent/${chat.id}`);
    }
  };

  const handleRename = async () => {
    if (!renaming || !renaming.title.trim()) return;
    setIsRenaming(true);
    try {
      const res = await renameQAgentChat(renaming.id, renaming.title.trim());
      if (res.success) {
        updateQAgentChatTitle(renaming.id, renaming.title.trim());
        setRenaming(null);
      } else {
        toast.error(t.nav.chats.renameFailed);
      }
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    setIsDeletingProject(true);
    try {
      const success = await deleteProjectStore(projectToDelete.id);
      if (success && activeProjectId === projectToDelete.id) {
        navigate('/');
      }
    } finally {
      setIsDeletingProject(false);
      setProjectToDelete(null);
    }
  };

  const isProjectActive = (projectId: string) => {
    return activeProjectId === projectId;
  };

  const isChatActive = (chatId: string) => {
    return routeChatId === chatId;
  };

  // Navigation handlers
  const handleNavClick = (path: string) => {
    setMeetingsDrawerOpen(false);
    navigate(path);
  };

  const handleMeetingsClick = () => {
    if (!meetingsDrawerOpen) {
      setMeetingsDrawerOpen(true);
    }
  };

  const handleLogout = async () => {
    await logout();
    removeAccessToken();
    removeRefreshToken();
    navigate('/login');
  };

  return (
    <>
      <SidebarRoot collapsible="icon">
        <SidebarHeader>
          <div className="flex items-center gap-2 px-2 py-2">
            <SidebarTrigger />
            <Separator orientation="vertical" className="h-6" />
            {workspaceLogoUrl ? (
              <img
                src={workspaceLogoUrl}
                alt={workspaceName ?? 'Workspace'}
                onClick={() => navigate('/')}
                className="h-8 max-w-[190px] object-contain cursor-pointer group-data-[collapsible=icon]:hidden"
              />
            ) : workspaceName ? (
              <span
                onClick={() => navigate('/')}
                className="text-lg font-semibold text-primary cursor-pointer group-data-[collapsible=icon]:hidden"
              >
                {workspaceName}
              </span>
            ) : (
              <img
                src={logo}
                alt="Queri"
                onClick={() => navigate('/')}
                className="h-7 cursor-pointer group-data-[collapsible=icon]:hidden"
              />
            )}
          </div>
        </SidebarHeader>

        <SidebarContent>
          {/* Primary actions and pages */}
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem data-tour="sidebar-new-chat">
                  <SidebarMenuButton
                    onClick={handleNewChat}
                    isActive={location.pathname === '/'}
                    tooltip={t.nav.tooltips.startBlankChat}
                  >
                    <Plus className="h-4 w-4" />
                    <span>{t.nav.items.newChat}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem data-tour="sidebar-new-contract">
                  <SidebarMenuButton
                    onClick={handleNewContract}
                    tooltip={t.nav.tooltips.draftNewContract}
                  >
                    <FilePlus className="h-4 w-4" />
                    <span>{t.nav.items.newContract}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem data-tour="sidebar-playbooks">
                  <SidebarMenuButton
                    onClick={() => handleNavClick('/playbooks')}
                    isActive={location.pathname.startsWith('/playbooks')}
                    tooltip={t.nav.items.playbooks}
                  >
                    <BookOpen />
                    <span>{t.nav.items.playbooks}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => handleNavClick('/compare')}
                    isActive={location.pathname.startsWith('/compare')}
                    tooltip={t.comparisons.nav}
                  >
                    <GitCompare />
                    <span>{t.comparisons.nav}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {isMeetingsEnabled && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={handleMeetingsClick}
                      tooltip={t.nav.items.meetings}
                    >
                      <Calendar />
                      <span>{t.nav.items.meetings}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <Separator />


          {/* Projects Section */}
          <SidebarGroup data-tour="sidebar-projects">
            <SidebarGroupLabel className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <FolderKanban className="h-3.5 w-3.5" />
                <span>{t.nav.groups.projects}</span>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => setShowNewProjectDialog(true)}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {t.nav.tooltips.createNewProject}
                </TooltipContent>
              </Tooltip>
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {isProjectsLoading && projects.length === 0 ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : projects.length === 0 ? (
                  <div className="px-2 py-3 text-center group-data-[collapsible=icon]:hidden">
                    <p className="text-xs text-muted-foreground/60">{t.nav.projects.none}</p>
                    <p className="text-[11px] text-muted-foreground/40 mt-0.5">{t.nav.projects.createToOrganize}</p>
                  </div>
                ) : (
                  projects.map((project) => {
                    const projectChats = chatsByProject.get(project.id) ?? [];
                    const expanded =
                      expandedProjects.has(project.id) || project.id === activeProjectId;
                    return (
                    <SidebarMenuItem key={project.id}>
                      <SidebarMenuButton
                        onClick={() => handleProjectClick(project.id)}
                        isActive={isProjectActive(project.id)}
                        tooltip={
                          project.access && project.access !== 'owner' && project.ownerName
                            ? `${project.name} · ${t.nav.share.sharedBy(project.ownerName)}`
                            : project.name
                        }
                        className="group"
                      >
                        <FolderKanban className="h-4 w-4 flex-shrink-0 group-data-[collapsible=icon]:block hidden" />
                        {projectChats.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                            onClick={(e) => toggleProject(project.id, e)}
                            className="-ml-1 flex h-4 w-4 items-center justify-center text-muted-foreground hover:text-foreground group-data-[collapsible=icon]:hidden"
                          >
                            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </span>
                        )}
                        <span className="truncate group-data-[collapsible=icon]:hidden">{project.kind === 'compare' ? t.comparisons.cp.mineTitle : project.name}</span>
                        {project.visibility && project.visibility !== 'private' && (
                          <UsersRound
                            className="h-3 w-3 flex-shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden"
                            aria-label={t.nav.share.sharedIcon}
                          />
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            asChild
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="ml-auto h-5 w-5 opacity-0 group-hover:opacity-100"
                            >
                              <MoreHorizontal className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleNewChatInProject(project.id);
                              }}
                            >
                              <MessageSquarePlus className="h-4 w-4 mr-2" />
                              {t.nav.chats.newInProject}
                            </DropdownMenuItem>
                            {(project.access ?? 'owner') === 'owner' && (
                              <>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSharingProject({ id: project.id, name: project.name });
                                  }}
                                >
                                  <UsersRound className="h-4 w-4 mr-2" />
                                  {t.nav.share.menu}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setProjectToDelete(project);
                                  }}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  {t.common.actions.delete}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </SidebarMenuButton>
                      {expanded && projectChats.length > 0 && (
                        <SidebarMenuSub>
                          {projectChats.slice(0, 8).map((chat) => (
                            <SidebarMenuSubItem key={chat.id} className="group/chat relative">
                              <SidebarMenuSubButton
                                onClick={() => handleQuickChatClick(chat.id)}
                                isActive={isChatActive(chat.id)}
                                className="pr-6"
                              >
                                <span className="truncate">
                                  {chat.title || chat.lastMessage?.substring(0, 30) || t.nav.chats.newChat}
                                </span>
                              </SidebarMenuSubButton>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-0 top-1/2 h-5 w-5 -translate-y-1/2 opacity-0 group-hover/chat:opacity-100"
                                  >
                                    <MoreHorizontal className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => setRenaming({ id: chat.id, title: chat.title || '' })}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    {t.nav.chats.rename}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={(e) => handleDeleteChat(chat.id, e as unknown as React.MouseEvent)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {t.common.actions.delete}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </SidebarMenuSubItem>
                          ))}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => void handleNewChatInProject(project.id)}
                              className="text-muted-foreground"
                            >
                              <Plus className="h-3 w-3" />
                              <span>{t.nav.chats.newChat}</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      )}
                    </SidebarMenuItem>
                    );
                  })
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <Separator />

          {/* Quick Chats Section */}
          <SidebarGroup>
            <SidebarGroupLabel>
              <div className="flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" />
                <span>{t.nav.groups.quickChats}</span>
              </div>
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {isLoadingChats ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : quickChats.length === 0 ? (
                  <div className="px-2 py-3 text-center group-data-[collapsible=icon]:hidden">
                    <p className="text-xs text-muted-foreground/60">{t.nav.chats.none}</p>
                    <p className="text-[11px] text-muted-foreground/40 mt-0.5">{t.nav.chats.startAbove}</p>
                  </div>
                ) : (
                  quickChats.slice(0, 10).map((chat) => (
                    <SidebarMenuItem key={chat.id}>
                      <SidebarMenuButton
                        onClick={() => handleQuickChatClick(chat.id)}
                        isActive={isChatActive(chat.id)}
                        tooltip={chat.title || t.nav.chats.untitled}
                        className="group"
                      >
                        <MessageSquare className="h-4 w-4 flex-shrink-0 group-data-[collapsible=icon]:block hidden" />
                        {chat.origin === 'email' && (
                          <Mail
                            className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden"
                            aria-label={t.nav.chats.fromEmail}
                          />
                        )}
                        <span className="truncate group-data-[collapsible=icon]:hidden">
                          {chat.title || chat.lastMessage?.substring(0, 30) || t.nav.chats.newChat}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            asChild
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="ml-auto h-5 w-5 opacity-0 group-hover:opacity-100"
                            >
                              <MoreHorizontal className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenaming({ id: chat.id, title: chat.title || '' });
                              }}
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              {t.nav.chats.rename}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => handleDeleteChat(chat.id, e as unknown as React.MouseEvent)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t.common.actions.delete}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Queri staff tools, below the user's own work */}
          {isQueriAdmin && (
            <>
              <Separator />
              <SidebarGroup>
                <SidebarGroupLabel>{t.nav.groups.admin}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => handleNavClick('/admin/organizations')}
                        isActive={location.pathname.startsWith('/admin/organizations')}
                        tooltip={t.nav.items.organizations}
                      >
                        <Building2 />
                        <span>{t.nav.items.organizations}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {canManageFeatures && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => handleNavClick('/features')}
                          isActive={location.pathname === '/features'}
                          tooltip={t.nav.items.features}
                        >
                          <ToggleLeft />
                          <span>{t.nav.items.features}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => handleNavClick('/admin/usage')}
                        isActive={location.pathname === '/admin/usage'}
                        tooltip={t.nav.items.usage}
                      >
                        <BarChart3 />
                        <span>{t.nav.items.usage}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => handleNavClick('/feedback/admin')}
                        isActive={location.pathname === '/feedback/admin'}
                        tooltip={t.nav.items.feedbackResponses}
                      >
                        <ClipboardList />
                        <span>{t.nav.items.feedbackResponses}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {canAccessModelSettings && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => handleNavClick('/admin/models')}
                          isActive={location.pathname === '/admin/models'}
                          tooltip={t.nav.items.modelSettings}
                        >
                          <Settings />
                          <span>{t.nav.items.modelSettings}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </>
          )}
        </SidebarContent>

        <SidebarFooter>
          <Separator />
          <div className="rounded-lg bg-sidebar-accent/50 p-1 group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:p-0">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => handleNavClick('/feedback')}
                  isActive={location.pathname === '/feedback'}
                  tooltip={t.nav.items.feedback}
                >
                  <MessageSquare />
                  <span>{t.nav.items.feedback}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem data-tour="sidebar-install-word">
                <SidebarMenuButton
                  onClick={() => window.open(WORD_ADDIN_MARKETPLACE_URL, '_blank', 'noopener,noreferrer')}
                  tooltip={t.nav.items.installForWord}
                >
                  <WordIcon className="h-4 w-4" />
                  <span>{t.nav.items.installForWord}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </div>
          <SidebarMenu>
            <SidebarMenuItem data-tour="sidebar-user-menu">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg">
                        {user?.firstName && user?.lastName ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : user?.email?.substring(0, 2).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email?.split('@')[0] || t.nav.user.fallbackName}
                      </span>
                      <span className="truncate text-xs">{user?.email || t.nav.user.fallbackEmail}</span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                  side="bottom"
                  align="end"
                  sideOffset={4}
                >
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                      <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarFallback className="rounded-lg">
                          {user?.firstName && user?.lastName ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : user?.email?.substring(0, 2).toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-semibold">
                          {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email?.split('@')[0] || t.nav.user.fallbackName}
                        </span>
                        <span className="truncate text-xs">{user?.email}</span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/account')}>
                    <UserCog className="mr-2 h-4 w-4" />
                    {t.account.menu}
                  </DropdownMenuItem>
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/members')}>
                      <UsersRound className="mr-2 h-4 w-4" />
                      {t.members.title}
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/cost')}>
                      <Wallet className="mr-2 h-4 w-4" />
                      {t.cost.menu}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <HelpCircle className="mr-2 h-4 w-4" />
                      <span>{t.onboarding.help.menuLabel}</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => launchTour('welcome')}>
                        {t.onboarding.help.restartWelcome}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => launchTour('review')}>
                        {t.onboarding.help.takeTour.review}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => launchTour('playbooks')}>
                        {t.onboarding.help.takeTour.playbooks}
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSeparator />
                  <LanguageSwitcher />
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut />
                    {t.common.actions.logOut}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
          {(workspaceName || workspaceLogoUrl) && <PoweredByQueri />}
        </SidebarFooter>
      </SidebarRoot>

      <ShareProjectDialog
        project={sharingProject}
        onClose={() => setSharingProject(null)}
        onSaved={() => void fetchProjects()}
      />

      {/* Rename chat */}
      <Dialog open={renaming != null} onOpenChange={(open) => !open && !isRenaming && setRenaming(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.nav.chats.renameTitle}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleRename();
            }}
            className="space-y-4"
          >
            <Input
              autoFocus
              maxLength={200}
              value={renaming?.title ?? ''}
              onChange={(e) => setRenaming((r) => (r ? { ...r, title: e.target.value } : r))}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenaming(null)} disabled={isRenaming}>
                {t.nav.chats.cancel}
              </Button>
              <Button type="submit" disabled={isRenaming || !renaming?.title.trim()}>
                {isRenaming && <Loader2 className="h-4 w-4 animate-spin" />}
                {t.nav.chats.save}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Project Dialog */}
      <Dialog open={showNewProjectDialog} onOpenChange={setShowNewProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.nav.newProjectDialog.title}</DialogTitle>
            <DialogDescription>
              {t.nav.newProjectDialog.description}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t.nav.newProjectDialog.nameLabel}</Label>
              <Input
                id="name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder={t.nav.newProjectDialog.namePlaceholder}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateProject();
                  }
                }}
              />
            </div>
            {trial && (
              <p className="text-xs text-muted-foreground">
                {t.nav.newProjectDialog.trialUsage(projects.length)}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewProjectDialog(false)}
            >
              {t.common.actions.cancel}
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={!newProjectName.trim() || isCreatingProject}
            >
              {isCreatingProject ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t.nav.newProjectDialog.creating}
                </>
              ) : (
                t.nav.newProjectDialog.submit
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Project Dialog */}
      <Dialog open={!!projectToDelete} onOpenChange={(open) => !open && setProjectToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.nav.deleteProjectDialog.title}</DialogTitle>
            <DialogDescription>
              {t.nav.deleteProjectDialog.description(projectToDelete?.name ?? '')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setProjectToDelete(null)}
              disabled={isDeletingProject}
            >
              {t.common.actions.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteProject}
              disabled={isDeletingProject}
            >
              {isDeletingProject ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t.nav.deleteProjectDialog.deleting}
                </>
              ) : (
                t.nav.deleteProjectDialog.confirm
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MeetingsDrawer
        open={meetingsDrawerOpen}
        onClose={() => setMeetingsDrawerOpen(false)}
      />
    </>
  );
};

export default QAgentSidebar;
